import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { History, X, Loader2, AlertTriangle, MessageSquareText } from 'lucide-react';
import { listarHistoricoSolicitacao, SolicitacaoHistoricoItem, STATUS_SOLICITACAO_LABEL } from '../../src/lib/cancelamentos';
import { linhaDoTempo, listarEnviosDaSolicitacao } from '../../src/lib/envioCancelamento';
import type { EventoLinhaDoTempo } from '../../src/lib/envioCancelamento';

interface SolicitacaoHistoricoPopoverProps {
    anchor: DOMRect;
    onClose: () => void;
    solicitacaoId: number;
}

const POPOVER_W = 300;

const formatData = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

/**
 * Linha do tempo da solicitação: mudanças de status (gravadas por trigger) mais
 * as mensagens enviadas ao cliente (#5830).
 *
 * As duas fontes são separadas de propósito — o trigger do histórico só dispara
 * quando `status` muda, e envio de mensagem não muda status. Juntar aqui evita
 * mexer num trigger SECURITY DEFINER que já roda em produção.
 */
const SolicitacaoHistoricoPopover: React.FC<SolicitacaoHistoricoPopoverProps> = ({ anchor, onClose, solicitacaoId }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState<{ top: number; left: number }>({ top: anchor.bottom + 6, left: anchor.left });
    const [loading, setLoading] = useState(true);
    const [itens, setItens] = useState<EventoLinhaDoTempo[]>([]);

    useEffect(() => {
        let ativo = true;
        // Se os envios falharem ao carregar, a linha do tempo ainda abre com os
        // status — perder o histórico inteiro por causa de uma tabela é pior.
        Promise.all([
            listarHistoricoSolicitacao(solicitacaoId),
            listarEnviosDaSolicitacao(solicitacaoId).catch(e => {
                console.error('Erro ao carregar envios da solicitação', e);
                return [] as any[];
            }),
        ])
            .then(([historico, envios]) => {
                if (!ativo) return;
                setItens(linhaDoTempo(
                    historico as any[],
                    envios as any[],
                    (status: string) => STATUS_SOLICITACAO_LABEL[status as keyof typeof STATUS_SOLICITACAO_LABEL] || status,
                ));
            })
            .catch(e => console.error('Erro ao carregar histórico da solicitação', e))
            .finally(() => { if (ativo) setLoading(false); });
        return () => { ativo = false; };
    }, [solicitacaoId]);

    useLayoutEffect(() => {
        const h = ref.current?.offsetHeight || 200;
        const margin = 8;
        let top = anchor.bottom + 6;
        if (top + h > window.innerHeight - margin) top = Math.max(margin, anchor.top - h - 6);
        let left = anchor.left - POPOVER_W + anchor.width;
        if (left + POPOVER_W > window.innerWidth - margin) left = window.innerWidth - POPOVER_W - margin;
        if (left < margin) left = margin;
        setPos({ top, left });
    }, [anchor, itens]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return createPortal(
        <div className="fixed inset-0 z-[2100]" onClick={onClose}>
            <div
                ref={ref}
                style={{ top: pos.top, left: pos.left, width: POPOVER_W }}
                className="fixed bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 p-4 animate-fade-in max-h-[70vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-bold text-gray-800 dark:text-white flex items-center gap-1.5">
                        <History size={14} className="text-gray-400" /> Histórico da Solicitação
                    </h4>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                        <X size={16} />
                    </button>
                </div>

                {loading ? (
                    <p className="text-xs text-gray-400 flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Carregando...</p>
                ) : itens.length === 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400">Sem histórico registrado.</p>
                ) : (
                    <ul className="text-xs text-gray-600 dark:text-gray-300 space-y-3">
                        {itens.map(item => (
                            <li key={item.chave} className={`border-l-2 pl-3 ${item.falhou ? 'border-red-400' : item.tipo === 'envio' ? 'border-info/60' : 'border-primary/40'}`}>
                                <span className="flex items-center gap-1 font-medium text-gray-800 dark:text-gray-100">
                                    {item.falhou
                                        ? <AlertTriangle size={11} className="text-red-500 shrink-0" aria-hidden="true" />
                                        : item.tipo === 'envio'
                                            ? <MessageSquareText size={11} className="text-info shrink-0" aria-hidden="true" />
                                            : <History size={11} className="text-gray-400 shrink-0" aria-hidden="true" />}
                                    {item.titulo}
                                </span>
                                <span className="block text-gray-400 dark:text-gray-500">{formatData(item.quando)}{item.autor ? ` · ${item.autor}` : ''}</span>
                                {item.detalhe && (
                                    <span className={`block mt-0.5 ${item.falhou ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400 italic'}`}>
                                        {item.detalhe}
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>,
        document.body
    );
};

export default SolicitacaoHistoricoPopover;
